import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Buildings,
  CheckCircle,
  FloppyDisk,
  GearSix,
  ImageSquare,
  MapPin,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import { myCompanyService } from '../../../services/companies/myCompanyService.js';
import { companyService } from '../../../services/companies/companyService.js';
import LocationPickerModal from '../../../shared/components/common/LocationPickerModal.jsx';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPE = 'image/png';

const COORDINATE_PATTERN = /^\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*,\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*$/;

const roundCoordinate = (value) => Math.round(value * 1000000) / 1000000;

const parseCoordinates = (value) => {
  if (!value?.trim()) {
    return { error: 'La latitud y la longitud son obligatorias.' };
  }
  const match = value.match(COORDINATE_PATTERN);
  if (!match) return { error: 'Ingresa coordenadas válidas en formato lat, lng.' };

  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { error: 'Ingresa coordenadas válidas en formato lat, lng.' };
  }
  if (lat < -90 || lat > 90) {
    return { error: 'La latitud debe estar entre -90 y 90.' };
  }
  if (lng < -180 || lng > 180) {
    return { error: 'La longitud debe estar entre -180 y 180.' };
  }

  return { coordinates: { lat, lng } };
};

const formatCoordinates = (lat, lng) =>
  lat !== null && lat !== undefined && lat !== ''
  && lng !== null && lng !== undefined && lng !== ''
    ? `${roundCoordinate(Number(lat))}, ${roundCoordinate(Number(lng))}`
    : '';

const normalizeCoordinates = (lat, lng) => {
  const coordinates = parseCoordinates(`${lat}, ${lng}`).coordinates;
  return coordinates
    ? { lat: roundCoordinate(coordinates.lat), lng: roundCoordinate(coordinates.lng) }
    : null;
};

const Alert = ({ notification }) => {
  if (!notification?.show) return null;

  const isSuccess = notification.type === 'success';
  return (
    <div
      className={`mb-5 flex items-center gap-3 rounded-lg border p-3 text-sm ${
        isSuccess
          ? 'border-success bg-success/12 text-success'
          : 'border-error bg-error/12 text-error'
      }`}
      role="alert"
    >
      {isSuccess ? <CheckCircle size={20} className="shrink-0" /> : <X size={20} className="shrink-0" />}
      <span>{notification.message}</span>
    </div>
  );
};

const ImagePreview = ({ src, label }) => (
  <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border border-line bg-bg">
    {src ? (
      <img src={src} alt={label} className="h-full w-full object-contain" />
    ) : (
      <div className="flex flex-col items-center gap-2 px-6 text-center text-content-muted">
        <Buildings size={42} weight="duotone" />
        <span className="text-sm">La empresa todavía no tiene una imagen.</span>
      </div>
    )}
  </div>
);

const CompanySettings = ({ onIconChanged, onNameChanged }) => {
  const fileInputRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [companyIcon, setCompanyIcon] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [currentIconUrl, setCurrentIconUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [companyName, setCompanyName] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [savingCompanyName, setSavingCompanyName] = useState(false);
  const [coordinatesInput, setCoordinatesInput] = useState('');
  const [coordinatesError, setCoordinatesError] = useState('');
  const [savingCoordinates, setSavingCoordinates] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapCoordinates, setMapCoordinates] = useState({ lat: '', lng: '' });

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 4000);
  }, []);

  const loadCompany = useCallback(async () => {
    setLoading(true);
    try {
      const response = await myCompanyService.getMyCompany();
      setCompany(response.company);
      setCompanyName(response.company.name ?? '');
      setCompanyNameError('');
      setCoordinatesInput(formatCoordinates(response.company.baseLat, response.company.baseLng));
      setCoordinatesError('');

      const icon = await myCompanyService.getMyCompanyIcon();
      setCompanyIcon(icon);
    } catch {
      showNotification('No se pudo cargar la configuración de la empresa.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    if (!companyIcon) {
      setCurrentIconUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(companyIcon);
    setCurrentIconUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [companyIcon]);

  const clearSelection = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== ACCEPTED_IMAGE_TYPE) {
      clearSelection();
      showNotification('Elige una imagen PNG.', 'error');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      clearSelection();
      showNotification('La imagen no puede superar los 5 MB.', 'error');
      return;
    }

    setSelectedFile(file);
  };

  const handleSave = async () => {
    if (!selectedFile || saving) return;

    setSaving(true);
    try {
      await myCompanyService.updateMyCompanyIcon(selectedFile);
      setCompanyIcon(selectedFile);
      onIconChanged?.(selectedFile);

      clearSelection();
      showNotification('La imagen de la empresa se actualizó correctamente.');
    } catch (error) {
      showNotification(error?.message || 'No se pudo actualizar la imagen. Inténtalo nuevamente.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCoordinatesInputChange = (event) => {
    const value = event.target.value;
    setCoordinatesInput(value);

    const result = parseCoordinates(value);
    setCoordinatesError(value.trim() && result.error ? result.error : '');
  };

  const handleSaveCompanyName = async () => {
    if (savingCompanyName || !company?.id) return;

    const name = companyName.trim();
    if (!name) {
      setCompanyNameError('El nombre de la empresa es obligatorio.');
      return;
    }

    setSavingCompanyName(true);
    try {
      await companyService.updateCompany(company.id, { name });
      setCompany((prev) => (prev ? { ...prev, name } : prev));
      setCompanyName(name);
      setCompanyNameError('');
      onNameChanged?.(name);
      showNotification('El nombre de la empresa se actualizó correctamente.');
    } catch (error) {
      showNotification(error?.message || 'No se pudo actualizar el nombre de la empresa. Inténtalo nuevamente.', 'error');
    } finally {
      setSavingCompanyName(false);
    }
  };

  const openMap = () => {
    const selected = parseCoordinates(coordinatesInput).coordinates;
    setMapCoordinates(selected ?? { lat: '', lng: '' });
    setMapOpen(true);
  };

  const handleSaveCoordinates = async () => {
    if (savingCoordinates || !company?.id) return;

    const result = parseCoordinates(coordinatesInput);
    if (result.error) {
      setCoordinatesError(result.error);
      return;
    }

    setSavingCoordinates(true);
    try {
      const { lat, lng } = result.coordinates;
      await companyService.updateCompany(company.id, {
        baseLat: roundCoordinate(lat),
        baseLng: roundCoordinate(lng)
      });
      setCompany((prev) => (prev ? { ...prev, baseLat: roundCoordinate(lat), baseLng: roundCoordinate(lng) } : prev));
      setCoordinatesInput(formatCoordinates(lat, lng));
      showNotification('Las coordenadas de la empresa se actualizaron correctamente.');
    } catch (error) {
      showNotification(error?.message || 'No se pudieron actualizar las coordenadas. Inténtalo nuevamente.', 'error');
    } finally {
      setSavingCoordinates(false);
    }
  };

  const originalCoordinates = formatCoordinates(company?.baseLat, company?.baseLng);
  const companyNameChanged = companyName.trim() !== (company?.name ?? '');
  const coordinatesChanged = coordinatesInput.trim() !== originalCoordinates;

  const displayedImage = previewUrl || currentIconUrl;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Configuración"
        subtitle="Personaliza la base operativa y la imagen identificatoria de tu empresa."
        icon={<GearSix size={28} className="text-content-muted" />}
      />

      <div className="w-full">
        <Alert notification={notification} />

        {loading ? (
          <div className="flex justify-center py-24 text-content-muted">Cargando configuración...</div>
        ) : (
          <>
          <section className="mb-6 rounded-xl border border-line bg-surface p-5 shadow-lg">
            <div className="mb-5 flex items-center gap-3">
              <div className="rounded-lg bg-surface-2 p-2 text-content-muted">
                <Buildings size={24} />
              </div>
              <div>
                <h2 className="font-semibold text-content">Nombre de la empresa</h2>
                <p className="text-sm text-content-muted">Define el nombre con el que se identifica tu empresa.</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex-1">
                <input
                  type="text"
                  value={companyName}
                  onChange={(event) => {
                    setCompanyName(event.target.value);
                    if (companyNameError) setCompanyNameError('');
                  }}
                  placeholder="Nombre de la empresa"
                  className={`w-full rounded-lg border bg-surface-2 px-4 py-2.5 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber ${
                    companyNameError ? 'border-error' : 'border-line'
                  }`}
                />
                {companyNameError && <p className="mt-1 text-sm text-error">{companyNameError}</p>}
              </div>

              <button
                type="button"
                onClick={handleSaveCompanyName}
                disabled={!companyNameChanged || savingCompanyName}
                className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                  companyNameChanged && !savingCompanyName
                    ? 'bg-amber text-on-amber hover:bg-amber-hover'
                    : 'cursor-not-allowed bg-surface-2 text-content-muted'
                }`}
              >
                <FloppyDisk size={18} />
                {savingCompanyName ? 'Guardando...' : 'Guardar nombre'}
              </button>
            </div>
          </section>

          <section className="mb-6 rounded-xl border border-line bg-surface p-5 shadow-lg">
            <div className="mb-5">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-surface-2 p-2 text-content-muted">
                  <MapPin size={24} />
                </div>
                <div>
                  <h2 className="font-semibold text-content">Base operativa</h2>
                  <p className="text-sm text-content-muted">Coordenadas de referencia de {company?.name || 'tu empresa'}. Punto de partida utilizado por los vehículos en el optimizador de rutas.</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    value={coordinatesInput}
                    onChange={handleCoordinatesInputChange}
                    placeholder="Ej: -31.5375, -68.5364"
                    className={`w-full rounded-lg border bg-surface-2 px-4 py-2.5 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber ${
                      coordinatesError ? 'border-error' : 'border-line'
                    }`}
                  />
                  {coordinatesError && (
                    <p className="mt-1 text-sm text-error">{coordinatesError}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={openMap}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-surface-2 px-4 py-2.5 text-sm font-semibold text-content transition-colors hover:bg-surface"
                  title="Seleccionar coordenadas en el mapa"
                ><MapPin size={18} /></button>

                <button
                  type="button"
                  onClick={handleSaveCoordinates}
                  disabled={!coordinatesChanged || savingCoordinates}
                  className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                    coordinatesChanged && !savingCoordinates
                      ? 'bg-amber text-on-amber hover:bg-amber-hover'
                      : 'cursor-not-allowed bg-surface-2 text-content-muted'
                  }`}
                >
                  <FloppyDisk size={18} />
                  {savingCoordinates ? '' : ''}
                </button>
              </div>
            </div>
          </section>

          <LocationPickerModal
            open={mapOpen}
            coordinates={mapCoordinates}
            onCoordinatesChange={setMapCoordinates}
            onClose={() => setMapOpen(false)}
            onConfirm={(coordinates) => {
              setCoordinatesInput(formatCoordinates(coordinates.lat, coordinates.lng));
              setCoordinatesError('');
              setMapOpen(false);
            }}
            normalizeCoordinates={normalizeCoordinates}
            markerId="company-settings-base-location"
            minZoom={13}
            maxBounds={[[-90, -180], [90, 180]]}
          />

          <section className="rounded-xl border border-line bg-surface p-5 shadow-lg">
            <div className="mb-5">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-surface-2 p-2 text-content-muted">
                  <ImageSquare size={24} />
                </div>
                <div>
                  <h2 className="font-semibold text-content">Imagen de la empresa</h2>
                  <p className="text-sm text-content-muted">Se utilizará como imagen identificatoria de {company?.name || 'tu empresa'}.</p>
                </div>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-[minmax(0,260px)_1fr]">
              <ImagePreview
                src={displayedImage}
                label={selectedFile ? 'Vista previa de la nueva imagen de la empresa' : 'Imagen actual de la empresa'}
              />

              <div className="flex flex-col justify-center">
                <input
                  ref={fileInputRef}
                  id="company-image"
                  type="file"
                  accept="image/png"
                  onChange={handleFileChange}
                  className="sr-only"
                />

                <label
                  htmlFor="company-image"
                  className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-line bg-bg/50 px-5 py-7 text-center transition-colors hover:border-amber hover:bg-amber/5"
                >
                  <UploadSimple size={30} className="mb-2 text-content-muted" />
                  <span className="font-medium text-content">Seleccionar imagen</span>
                  <span className="mt-1 text-xs text-content-muted">PNG · Máximo 5 MB</span>
                </label>

                {selectedFile && (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-surface-2/70 px-3 py-2 text-sm">
                    <span className="min-w-0 break-all whitespace-normal text-content" title={selectedFile.name}>{selectedFile.name}</span>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="shrink-0 rounded p-1 text-content-muted transition-colors hover:bg-surface-2 hover:text-content"
                      title="Quitar imagen seleccionada"
                      aria-label="Quitar imagen seleccionada"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!selectedFile || saving}
                  className={`mt-5 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                    selectedFile && !saving
                      ? 'bg-amber text-on-amber hover:bg-amber-hover'
                      : 'cursor-not-allowed bg-surface-2 text-content-muted'
                  }`}
                >
                  <FloppyDisk size={18} />
                  {saving ? 'Guardando...' : 'Guardar imagen'}
                </button>
              </div>
            </div>
          </section>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanySettings;
